<script lang="ts">
  import { createEventDispatcher, onDestroy, onMount, tick } from 'svelte';
  import {
    BRUSH_SIZES,
    CANVAS_PALETTE,
    WORLD_HEIGHT,
    WORLD_WIDTH,
    appendStrokePoint,
    initialTransform,
    panBy,
    pinchTransform,
    replayPlan,
    replayProgress,
    screenToWorld,
    zoomAt,
    type CanvasTransform,
    type Point,
    type ReplayPlan
  } from '../vidamorCanvasCore';
  import { addCanvasStroke, fetchCanvasStrokes, type CanvasStroke } from '../../lib/convexApi';
  import { liveCanvasTailStore } from '../../lib/convexRealtime';

  export let canvasKey: string;

  const dispatch = createEventDispatcher<{ close: void }>();

  // --- stroke state ---------------------------------------------------------
  let history: CanvasStroke[] = [];
  let tail: CanvasStroke[] = [];
  let pending: CanvasStroke[] = []; // our optimistic, in-flight strokes
  const seenIds = new Set<string>();
  let loading = true;
  let loadError: string | null = null;
  let saveError: string | null = null;
  let saveErrorTimer: ReturnType<typeof setTimeout> | null = null;

  let tailStore: ReturnType<typeof liveCanvasTailStore> | null = null;
  let tailUnsub: (() => void) | null = null;
  let anchor = 0;

  // Merge the live tail into history and move the subscription anchor forward
  // once the tail grows, so each reactive update stays a small payload.
  const REANCHOR_AT = 150;

  function absorbTail(strokes: CanvasStroke[]) {
    const fresh = strokes.filter(s => !seenIds.has(s.id));
    for (const s of fresh) seenIds.add(s.id);
    if (fresh.length > 0) {
      tail = [...tail, ...fresh];
      // A confirmed stroke replaces its optimistic twin.
      pending = pending.filter(p => !seenIds.has(p.id));
      scheduleDraw();
    }
    if (tail.length >= REANCHOR_AT) {
      history = [...history, ...tail];
      anchor = tail[tail.length - 1].createdAt;
      tail = [];
      void tailStore?.setArgs({ canvasKey, after: anchor });
    }
  }

  async function loadCanvas() {
    loading = true;
    loadError = null;
    try {
      const strokes = await fetchCanvasStrokes(canvasKey);
      history = strokes;
      for (const s of strokes) seenIds.add(s.id);
      anchor = strokes.length > 0 ? strokes[strokes.length - 1].createdAt : 0;
      tailStore = liveCanvasTailStore(canvasKey, anchor);
      tailUnsub = tailStore.subscribe(state => {
        if (state.data) absorbTail(state.data);
      });
    } catch (err) {
      loadError = err instanceof Error ? err.message : 'Could not load the wall.';
    } finally {
      loading = false;
      scheduleDraw();
    }
  }

  // --- brush ---------------------------------------------------------------
  let color: string = CANVAS_PALETTE[1]; // rust
  let brush: number = BRUSH_SIZES[1];
  let tool: 'draw' | 'move' = 'draw';

  // --- viewport / rendering -------------------------------------------------
  let stageEl: HTMLDivElement;
  let canvasEl: HTMLCanvasElement;
  let ctx: CanvasRenderingContext2D | null = null;
  let viewW = 0;
  let viewH = 0;
  let transform: CanvasTransform = { scale: 1, ox: 0, oy: 0 };
  let drawQueued = false;
  let resizeObserver: ResizeObserver | null = null;

  function measure(first = false) {
    if (!stageEl || !canvasEl) return;
    const rect = stageEl.getBoundingClientRect();
    viewW = rect.width;
    viewH = rect.height;
    const dpr = window.devicePixelRatio || 1;
    canvasEl.width = Math.round(viewW * dpr);
    canvasEl.height = Math.round(viewH * dpr);
    ctx = canvasEl.getContext('2d');
    if (first) transform = initialTransform(viewW, viewH);
    else transform = panBy(transform, 0, 0, viewW, viewH); // re-clamp
    scheduleDraw();
  }

  function scheduleDraw() {
    if (drawQueued) return;
    drawQueued = true;
    requestAnimationFrame(() => {
      drawQueued = false;
      draw();
    });
  }

  function drawStroke(c: CanvasRenderingContext2D, s: { color: string; size: number; points: number[] }) {
    const p = s.points;
    if (p.length < 2) return;
    c.strokeStyle = s.color;
    c.fillStyle = s.color;
    c.lineWidth = s.size;
    c.lineCap = 'round';
    c.lineJoin = 'round';
    if (p.length === 2) {
      c.beginPath();
      c.arc(p[0], p[1], s.size / 2, 0, Math.PI * 2);
      c.fill();
      return;
    }
    c.beginPath();
    c.moveTo(p[0], p[1]);
    for (let i = 2; i < p.length; i += 2) c.lineTo(p[i], p[i + 1]);
    c.stroke();
  }

  /** Draw only the first `fraction` of a stroke — the replay's growing line. */
  function drawPartialStroke(
    c: CanvasRenderingContext2D,
    s: { color: string; size: number; points: number[] },
    fraction: number
  ) {
    const totalPoints = s.points.length / 2;
    const shown = Math.max(1, Math.round(fraction * totalPoints));
    drawStroke(c, { ...s, points: s.points.slice(0, shown * 2) });
  }

  function draw() {
    if (!ctx) return;
    const dpr = window.devicePixelRatio || 1;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    // the void around the paper
    ctx.fillStyle = '#2b201a';
    ctx.fillRect(0, 0, canvasEl.width, canvasEl.height);
    ctx.setTransform(
      transform.scale * dpr,
      0,
      0,
      transform.scale * dpr,
      transform.ox * dpr,
      transform.oy * dpr
    );
    // the paper
    ctx.fillStyle = '#fbf0e1';
    ctx.fillRect(0, 0, WORLD_WIDTH, WORLD_HEIGHT);
    if (replaying && plan) {
      const progress = replayProgress(plan, performance.now() - replayStart);
      for (let i = 0; i < progress.complete && i < replayList.length; i++) {
        drawStroke(ctx, replayList[i]);
      }
      if (progress.fraction !== null && progress.complete < replayList.length) {
        drawPartialStroke(ctx, replayList[progress.complete], progress.fraction);
      }
      return;
    }
    for (const s of history) drawStroke(ctx, s);
    for (const s of tail) drawStroke(ctx, s);
    for (const s of pending) drawStroke(ctx, s);
    if (current) drawStroke(ctx, current);
  }

  // --- replay ---------------------------------------------------------------
  let replaying = false;
  let plan: ReplayPlan | null = null;
  let replayList: CanvasStroke[] = [];
  let replayStart = 0;

  function startReplay() {
    replayList = [...history, ...tail, ...pending]; // already oldest-first
    if (replayList.length === 0) return;
    plan = replayPlan(replayList);
    replayStart = performance.now();
    replaying = true;
    requestAnimationFrame(tickReplay);
  }

  function tickReplay() {
    if (!replaying || !plan) return;
    draw();
    if (performance.now() - replayStart >= plan.total + 400) {
      stopReplay();
      return;
    }
    requestAnimationFrame(tickReplay);
  }

  function stopReplay() {
    replaying = false;
    plan = null;
    scheduleDraw();
  }

  // --- gestures --------------------------------------------------------------
  interface TrackedPointer extends Point {
    id: number;
  }
  let pointers: TrackedPointer[] = [];
  let current: { color: string; size: number; points: number[] } | null = null;
  let pinching = false;

  function localPoint(event: PointerEvent): Point {
    const rect = canvasEl.getBoundingClientRect();
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  }

  function onPointerDown(event: PointerEvent) {
    if (event.pointerType === 'mouse' && event.button !== 0 && event.button !== 1) return;
    if (replaying) {
      // any touch skips to the finished wall
      stopReplay();
      return;
    }
    try {
      canvasEl.setPointerCapture(event.pointerId);
    } catch {
      // capture is best-effort: a pointer released in the same frame throws here
    }
    const p = localPoint(event);
    pointers = [...pointers, { id: event.pointerId, x: p.x, y: p.y }];

    if (pointers.length === 2) {
      // second finger lands: this is a pinch, not a line — drop the partial stroke
      current = null;
      pinching = true;
      scheduleDraw();
      return;
    }
    if (pointers.length > 2) return;

    const panGesture = tool === 'move' || (event.pointerType === 'mouse' && event.button === 1);
    if (!panGesture) {
      const w = screenToWorld(transform, p.x, p.y);
      current = { color, size: brush, points: [] };
      strokeStartedAt = performance.now();
      appendStrokePoint(current.points, w.x, w.y);
      scheduleDraw();
    }
  }

  function onPointerMove(event: PointerEvent) {
    const index = pointers.findIndex(entry => entry.id === event.pointerId);
    if (index === -1) return;
    const p = localPoint(event);

    if (pinching && pointers.length >= 2) {
      const prevA = { x: pointers[0].x, y: pointers[0].y };
      const prevB = { x: pointers[1].x, y: pointers[1].y };
      const nextA = index === 0 ? p : prevA;
      const nextB = index === 1 ? p : prevB;
      transform = pinchTransform(transform, prevA, prevB, nextA, nextB, viewW, viewH);
      pointers[index] = { ...pointers[index], ...p };
      pointers = pointers;
      scheduleDraw();
      return;
    }

    if (current) {
      const w = screenToWorld(transform, p.x, p.y);
      if (!appendStrokePoint(current.points, w.x, w.y)) {
        // stroke is full: ship it and keep painting in a fresh one
        const lastX = current.points[current.points.length - 2];
        const lastY = current.points[current.points.length - 1];
        finishStroke();
        current = { color, size: brush, points: [lastX, lastY] };
        strokeStartedAt = performance.now();
        appendStrokePoint(current.points, w.x, w.y);
      }
      pointers[index] = { ...pointers[index], ...p };
      pointers = pointers;
      scheduleDraw();
      return;
    }

    // move tool (or middle-drag): pan
    const prev = pointers[index];
    transform = panBy(transform, p.x - prev.x, p.y - prev.y, viewW, viewH);
    pointers[index] = { ...prev, ...p };
    pointers = pointers;
    scheduleDraw();
  }

  function onPointerUp(event: PointerEvent) {
    pointers = pointers.filter(entry => entry.id !== event.pointerId);
    if (pointers.length < 2) pinching = false;
    if (current && pointers.length === 0) finishStroke();
  }

  let tempIdCounter = 0;
  let strokeStartedAt = 0;

  function finishStroke() {
    if (!current) return;
    const stroke = current;
    current = null;
    if (stroke.points.length < 2) return;
    const durationMs = Math.max(0, Math.round(performance.now() - strokeStartedAt));
    const tempId = `local-${++tempIdCounter}`;
    const optimistic: CanvasStroke = { id: tempId, ...stroke, durationMs, createdAt: Date.now() };
    pending = [...pending, optimistic];
    scheduleDraw();
    void addCanvasStroke({ canvasKey, color: stroke.color, size: stroke.size, points: stroke.points, durationMs })
      .then(({ id }) => {
        // keep it rendered under its real id until the tail confirms it
        if (!seenIds.has(id)) {
          pending = pending.map(p => (p.id === tempId ? { ...p, id } : p));
        } else {
          pending = pending.filter(p => p.id !== tempId);
        }
      })
      .catch(err => {
        pending = pending.filter(p => p.id !== tempId);
        scheduleDraw();
        if (saveErrorTimer) clearTimeout(saveErrorTimer);
        saveError = err instanceof Error && /auth/i.test(err.message)
          ? 'sign in to leave your mark'
          : "that stroke didn't stick — try again";
        saveErrorTimer = setTimeout(() => (saveError = null), 3200);
      });
  }

  function onWheel(event: WheelEvent) {
    event.preventDefault();
    const rect = canvasEl.getBoundingClientRect();
    const factor = Math.exp(-event.deltaY * 0.0016);
    transform = zoomAt(transform, factor, event.clientX - rect.left, event.clientY - rect.top, viewW, viewH);
    scheduleDraw();
  }

  function zoomButtons(factor: number) {
    transform = zoomAt(transform, factor, viewW / 2, viewH / 2, viewW, viewH);
    scheduleDraw();
  }

  function resetView() {
    transform = initialTransform(viewW, viewH);
    scheduleDraw();
  }

  function onKeydown(event: KeyboardEvent) {
    if (event.key === 'Escape') dispatch('close');
  }

  let closeBtn: HTMLButtonElement;

  onMount(async () => {
    document.body.style.overflow = 'hidden';
    await tick();
    measure(true);
    resizeObserver = new ResizeObserver(() => measure());
    resizeObserver.observe(stageEl);
    void loadCanvas();
    closeBtn?.focus();
  });

  onDestroy(() => {
    document.body.style.overflow = '';
    resizeObserver?.disconnect();
    tailUnsub?.();
    tailStore?.dispose();
    if (saveErrorTimer) clearTimeout(saveErrorTimer);
  });
</script>

<svelte:window on:keydown={onKeydown} />

<div class="wall" role="dialog" aria-modal="true" aria-label="the vidamor wall — draw together">
  <header class="wall__bar">
    <p class="wall__title">the wall <span class="wall__hint">— everyone draws on the same one. be nice(ish).</span></p>
    <div class="wall__bar-actions">
      {#if !loading && history.length + tail.length + pending.length > 0}
        <button type="button" class="wall__replay" on:click={() => (replaying ? stopReplay() : startReplay())}>
          {replaying ? '■ skip' : '▶ replay'}
        </button>
      {/if}
      <button type="button" class="wall__close" bind:this={closeBtn} on:click={() => dispatch('close')}>
        done
      </button>
    </div>
  </header>

  <div class="wall__stage" bind:this={stageEl}>
    <canvas
      bind:this={canvasEl}
      class="wall__canvas"
      class:wall__canvas--move={tool === 'move'}
      on:pointerdown={onPointerDown}
      on:pointermove={onPointerMove}
      on:pointerup={onPointerUp}
      on:pointercancel={onPointerUp}
      on:wheel={onWheel}
    ></canvas>

    {#if loading}
      <p class="wall__status">unrolling the wall…</p>
    {:else if loadError}
      <p class="wall__status">
        {loadError}
        <button type="button" class="wall__retry" on:click={loadCanvas}>retry</button>
      </p>
    {/if}
    {#if saveError}
      <p class="wall__toast">{saveError}</p>
    {/if}

    <div class="wall__viewtools">
      <button type="button" aria-label="Zoom in" on:click={() => zoomButtons(1.4)}>+</button>
      <button type="button" aria-label="Zoom out" on:click={() => zoomButtons(1 / 1.4)}>−</button>
      <button type="button" aria-label="Fit the whole wall" on:click={resetView}>⌂</button>
    </div>
  </div>

  <footer class="wall__tools">
    <div class="wall__swatches" role="group" aria-label="Brush color">
      {#each CANVAS_PALETTE as swatch (swatch)}
        <button
          type="button"
          class="wall__swatch"
          class:wall__swatch--active={color === swatch}
          style={`background:${swatch}`}
          aria-label={`Color ${swatch}`}
          aria-pressed={color === swatch}
          on:click={() => { color = swatch; tool = 'draw'; }}
        ></button>
      {/each}
    </div>
    <div class="wall__right">
      <div class="wall__sizes" role="group" aria-label="Brush size">
        {#each BRUSH_SIZES as size (size)}
          <button
            type="button"
            class="wall__size"
            class:wall__size--active={brush === size}
            aria-label={`Brush size ${size}`}
            aria-pressed={brush === size}
            on:click={() => { brush = size; tool = 'draw'; }}
          >
            <span style={`width:${Math.min(size, 20)}px;height:${Math.min(size, 20)}px;`}></span>
          </button>
        {/each}
      </div>
      <div class="wall__modes" role="group" aria-label="Tool">
        <button
          type="button"
          class="wall__mode"
          class:wall__mode--active={tool === 'draw'}
          aria-pressed={tool === 'draw'}
          on:click={() => (tool = 'draw')}
        >draw</button>
        <button
          type="button"
          class="wall__mode"
          class:wall__mode--active={tool === 'move'}
          aria-pressed={tool === 'move'}
          on:click={() => (tool = 'move')}
        >move</button>
      </div>
    </div>
  </footer>
</div>

<style>
  .wall {
    position: fixed;
    inset: 0;
    z-index: 110;
    display: flex;
    flex-direction: column;
    background: #2b201a;
    color: #fbf0e1;
    font-family: var(--font-mono);
  }

  .wall__bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--space-3);
    padding: var(--space-3) var(--space-4);
  }

  .wall__title {
    margin: 0;
    font-size: 14px;
    letter-spacing: 0.04em;
  }

  .wall__hint {
    opacity: 0.55;
    font-size: 12px;
  }

  .wall__bar-actions {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    flex-shrink: 0;
  }

  .wall__replay {
    background: transparent;
    color: #fbf0e1;
    border: 1px solid rgb(251 240 225 / 0.4);
    border-radius: 999px;
    padding: var(--space-2) var(--space-4);
    font-family: var(--font-mono);
    font-size: 13px;
    cursor: pointer;
  }

  .wall__close {
    background: #fbf0e1;
    color: #3a2c22;
    border: none;
    border-radius: 999px;
    padding: var(--space-2) var(--space-5);
    font-family: var(--font-mono);
    font-size: 13px;
    cursor: pointer;
  }

  .wall__stage {
    position: relative;
    flex: 1;
    min-height: 0;
  }

  .wall__canvas {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    touch-action: none;
    cursor: crosshair;
  }

  .wall__canvas--move {
    cursor: grab;
  }

  .wall__status {
    position: absolute;
    top: var(--space-4);
    left: 50%;
    transform: translateX(-50%);
    margin: 0;
    font-size: 13px;
    background: rgb(43 32 26 / 0.85);
    border-radius: 999px;
    padding: var(--space-2) var(--space-4);
  }

  .wall__retry {
    background: none;
    border: none;
    color: #f0a566;
    font-family: var(--font-mono);
    font-size: 13px;
    text-decoration: underline;
    cursor: pointer;
  }

  .wall__toast {
    position: absolute;
    bottom: var(--space-4);
    left: 50%;
    transform: translateX(-50%);
    margin: 0;
    font-size: 13px;
    color: #fbf0e1;
    background: rgb(166 75 46 / 0.92);
    border-radius: 999px;
    padding: var(--space-2) var(--space-4);
    white-space: nowrap;
  }

  .wall__viewtools {
    position: absolute;
    right: var(--space-3);
    top: var(--space-3);
    display: flex;
    flex-direction: column;
    gap: var(--space-1);
  }

  .wall__viewtools button {
    width: 36px;
    height: 36px;
    border-radius: 9px;
    border: 1px solid rgb(251 240 225 / 0.25);
    background: rgb(43 32 26 / 0.8);
    color: #fbf0e1;
    font-size: 16px;
    cursor: pointer;
  }

  .wall__tools {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--space-3);
    padding: var(--space-3) var(--space-4);
    padding-bottom: max(var(--space-3), env(safe-area-inset-bottom));
    flex-wrap: wrap;
  }

  .wall__swatches {
    display: flex;
    gap: var(--space-2);
    flex-wrap: wrap;
  }

  .wall__swatch {
    width: 30px;
    height: 30px;
    border-radius: 50%;
    border: 2px solid rgb(251 240 225 / 0.25);
    cursor: pointer;
    padding: 0;
  }

  .wall__swatch--active {
    border-color: #fbf0e1;
    transform: scale(1.15);
  }

  .wall__right {
    display: flex;
    align-items: center;
    gap: var(--space-3);
  }

  .wall__sizes {
    display: flex;
    gap: var(--space-1);
  }

  .wall__size {
    width: 34px;
    height: 34px;
    border-radius: 9px;
    border: 1px solid rgb(251 240 225 / 0.25);
    background: transparent;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    padding: 0;
  }

  .wall__size span {
    display: block;
    border-radius: 50%;
    background: #fbf0e1;
  }

  .wall__size--active {
    border-color: #fbf0e1;
    background: rgb(251 240 225 / 0.12);
  }

  .wall__modes {
    display: flex;
    border: 1px solid rgb(251 240 225 / 0.25);
    border-radius: 999px;
    overflow: hidden;
  }

  .wall__mode {
    background: transparent;
    border: none;
    color: #fbf0e1;
    font-family: var(--font-mono);
    font-size: 12px;
    padding: var(--space-2) var(--space-3);
    cursor: pointer;
  }

  .wall__mode--active {
    background: #fbf0e1;
    color: #3a2c22;
  }
</style>
